import {
  FieldHtmlNameUtil,
  HekateDisplayField,
  HekateOptionList,
  type HekateDisplayForm,
} from '@onshape/www-inertia';
import FormFieldCoordinator from './FormFieldCoordinator';
import type { SetupContext } from 'vue';

type DatePartInput = {
  isValid: boolean;
  isErrored: boolean;
  isTouched: boolean;
};

export default class AgeVerificationFieldCoordinator extends FormFieldCoordinator {
  constructor({
    context,
    field,
    modelValue,
    formDefinition,
  }: {
    field: Ref<HekateDisplayField>;
    context: SetupContext;
    modelValue: Ref<string>;
    formDefinition: Ref<HekateDisplayForm>;
  }) {
    super({
      context,
      field,
      modelValue,
      formDefinition,
    });
  }

  private createDateFieldInput(
    min: number,
    max: number,
    field: Ref<string>,
    minLength: number = 2
  ) {
    const localIsTouched = ref(false);

    const unwatch = watch(field, (newVal) => {
      if (localIsTouched.value || newVal === '') return;

      if (parseInt(newVal, 10) < min) {
        return;
      }
      localIsTouched.value = true;
      unwatch();
    });

    const result: Ref<DatePartInput> = computed(() => {
      if (!localIsTouched.value) {
        return {
          isValid: false,
          isErrored: false,
          isTouched: false,
        };
      }

      if (field.value === '' && !localIsTouched.value) {
        return {
          isValid: false,
          isErrored: false,
          isTouched: localIsTouched.value,
        };
      }

      if (field.value === '' && localIsTouched.value) {
        return {
          isValid: false,
          isErrored: true,
          isTouched: true,
        };
      }

      if (field.value.length < minLength) {
        return {
          isValid: false,
          isErrored: false,
          isTouched: localIsTouched.value,
        };
      }

      const intValue = parseInt(field.value, 10);
      const isValid = !isNaN(intValue) && intValue >= min && intValue <= max;
      const isErrored = field.value !== '' && (isNaN(intValue) || intValue < min || intValue > max);

      return {
        isValid,
        isErrored,
        isTouched: localIsTouched.value,
      };
    });

    return result;
  }

  setup() {
    const { heartbeat } = useCrossSession();

    const dd = ref('');
    const mm = ref('');
    const yyyy = ref('');

    const ddInput = this.createDateFieldInput(1, 31, dd);
    const mmInput = this.createDateFieldInput(1, 12, mm);
    const yyyyInput = this.createDateFieldInput(1900, new Date().getFullYear(), yyyy);

    const bubbleModel = this.buildBubbleModel();
    watch([dd, mm, yyyy], () => {
      if (!ddInput.value.isTouched || !mmInput.value.isTouched || !yyyyInput.value.isTouched) {
        return;
      }

      if (ddInput.value.isErrored || mmInput.value.isErrored || yyyyInput.value.isErrored) {
        bubbleModel.value = 'invalidDateFormat';
        return;
      }

      try {
        const invalidDate = new Date(
          parseInt(yyyy.value, 10),
          parseInt(mm.value, 10) - 1,
          parseInt(dd.value, 10)
        );

        if (isNaN(invalidDate.getTime())) {
          bubbleModel.value = 'invalidDateFormat';
          return;
        }
      } catch (e) {
        bubbleModel.value = 'invalidDateFormat';
        console.warn('Error parsing age verification values', e);
        return;
      }

      const month = mm.value.padStart(2, '0');
      const day = dd.value.padStart(2, '0');
      const year = yyyy.value;

      try {
        const today = new Date(heartbeat.value?.now || Date.now());
        let age = today.getFullYear() - parseInt(year, 10);
        const monthDiff = today.getMonth() - parseInt(month, 10) + 1;
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parseInt(day, 10))) {
          age -= 1;
        }
        const isUnder13 = age < 13;
        if (isUnder13) {
          bubbleModel.value = 'underage';
          return;
        }
        bubbleModel.value = 'overage';
      } catch (e) {
        bubbleModel.value = 'underage';
        console.error('Error parsing age verification values', e);
      }
    });

    const cleanOnBlur = () => {
      // remove all non-digit characters from the year input and limit lenght of the input
      dd.value = dd.value.replace(/\D/g, '').slice(0, 2);
      mm.value = mm.value.replace(/\D/g, '').slice(0, 2);
      yyyy.value = yyyy.value.replace(/\D/g, '').slice(0, 4);

      if (dd.value.length === 1) {
        dd.value = dd.value.padStart(2, '0');
      }
      if (mm.value.length === 1) {
        mm.value = mm.value.padStart(2, '0');
      }

      // if the user puts in a number of days that are not possible for that
      // date, reset the day and month inputs
      const day = parseInt(dd.value, 10);
      const month = parseInt(mm.value, 10);
      const year = parseInt(yyyy.value, 10);
      if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
        const maxDays = new Date(year, month, 0).getDate();
        if (day > maxDays) {
          dd.value = '';
          mm.value = '';
        }
      }

      // if the year is unrealistically high or low, reset the year input
      if (year < 1900 || year > new Date(heartbeat.value?.now || Date.now()).getFullYear()) {
        yyyy.value = '';
      }
    };

    return {
      bubbleModel,
      dd,
      ddInput,
      mm,
      mmInput,
      yyyy,
      yyyyInput,
      cleanOnBlur,
    };
  }
}

export const buildAgeVerificationField = () => {
  return new HekateDisplayField({
    id: 'age-verification',
    name: 'age-verification',
    displayType: 'age-verification',
    displayVariantType: 'field-group-body',
    dataType: 'age-verification',
    htmlName: FieldHtmlNameUtil.ageVerification,
    label: 'Age Verification',
    validations: [],
    optionList: HekateOptionList.newEmptyList(),
    optionListId: '',
  });
};
